import { useCallback } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from '@/lib/icons'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { BloggerListItem, BloggerListQuery } from '@ayub/shared'
import { formatNumber, formatSom } from '@ayub/shared'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { BLOGGERS_PAGE_SIZE } from './api'
import { BloggersFirstRunEmptyState, BloggersNoResultsEmptyState } from './EmptyStates'
import { BloggersErrorBanner, BloggersRetryState } from './ErrorStates'
import { Pagination } from './Pagination'
import { StageBadge } from './StageBadge'

type SortKey = NonNullable<BloggerListQuery['sort']>
type SortOrder = NonNullable<BloggerListQuery['order']>

const SORTABLE_COLUMNS = new Set<string>(['fullName', 'followers', 'totalEarned'])

const columnHelper = createColumnHelper<BloggerListItem>()

const columns = [
  columnHelper.accessor('fullName', {
    id: 'fullName',
    header: 'Bloger',
    cell: (info) => <span className="font-medium text-app-text">{info.getValue()}</span>,
  }),
  columnHelper.accessor('instagramUsername', {
    id: 'instagramUsername',
    header: 'Instagram',
    enableSorting: false,
    cell: (info) => <span className="text-app-hint">@{info.getValue()}</span>,
  }),
  columnHelper.accessor('followers', {
    id: 'followers',
    header: 'Obunachi',
    cell: (info) => <span className="tabular">{formatNumber(info.getValue())}</span>,
  }),
  columnHelper.accessor('videoCount', {
    id: 'videoCount',
    header: 'Video',
    enableSorting: false,
    cell: (info) => <span className="tabular">{formatNumber(info.getValue())}</span>,
  }),
  columnHelper.accessor('totalEarned', {
    id: 'totalEarned',
    header: 'Ishlagan',
    cell: (info) => <span className="tabular">{formatSom(info.getValue())}</span>,
  }),
  columnHelper.accessor('stage', {
    id: 'stage',
    header: 'Holat',
    enableSorting: false,
    cell: (info) => <StageBadge stage={info.getValue()} />,
  }),
]

interface BloggerListViewProps {
  bloggers: BloggerListItem[]
  total: number
  page: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  sort: SortKey
  order: SortOrder
  searchTerm: string
  hasActiveFilters: boolean
  onSortChange: (sort: SortKey, order: SortOrder) => void
  onPageChange: (page: number) => void
  onSelectBlogger: (id: number) => void
  onRetry: () => void
  onClearFilters: () => void
}

/**
 * List ko'rinishi — `docs/03-app-flow.md` Sahifa 3 standart ko'rinishi. Desktopda TanStack
 * Table jadval, `< 640px`da kartochkalar ro'yxati (`docs/04-ui-ux.md` "Jadval (List
 * ko'rinish)": "Mobil: jadval kartochkalar ro'yxatiga aylanadi").
 */
export function BloggerListView({
  bloggers,
  total,
  page,
  isLoading,
  isFetching,
  error,
  sort,
  order,
  searchTerm,
  hasActiveFilters,
  onSortChange,
  onPageChange,
  onSelectBlogger,
  onRetry,
  onClearFilters,
}: BloggerListViewProps) {
  const table = useReactTable({
    data: bloggers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleHeaderClick = useCallback(
    (columnId: string) => {
      if (!SORTABLE_COLUMNS.has(columnId)) return
      const key = columnId as SortKey
      if (sort === key) {
        onSortChange(key, order === 'asc' ? 'desc' : 'asc')
      } else {
        onSortChange(key, 'asc')
      }
    },
    [sort, order, onSortChange],
  )

  if (isLoading) {
    return <BloggerListSkeleton />
  }

  if (error && bloggers.length === 0) {
    return <BloggersRetryState error={error} onRetry={onRetry} />
  }

  if (total === 0) {
    return hasActiveFilters ? (
      <BloggersNoResultsEmptyState searchTerm={searchTerm} onClearFilters={onClearFilters} />
    ) : (
      <BloggersFirstRunEmptyState />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <BloggersErrorBanner error={error} isFetching={isFetching} onRetry={onRetry} />}

      {/* Desktop / tablet: jadval */}
      <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-app-card">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortable = SORTABLE_COLUMNS.has(header.column.id)
                  const isActive = sort === header.column.id
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint"
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => handleHeaderClick(header.column.id)}
                          className="inline-flex items-center gap-1 hover:text-app-text"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <SortIcon active={isActive} order={isActive ? order : undefined} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                tabIndex={0}
                role="button"
                aria-label={`${row.original.fullName} — batafsil ko'rish`}
                onClick={() => onSelectBlogger(row.original.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelectBlogger(row.original.id)
                  }
                }}
                className="h-12 cursor-pointer border-b border-app-border last:border-b-0 hover:bg-app-card focus-visible:bg-app-card"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: kartochkalar ro'yxati */}
      <div className="flex flex-col gap-2 sm:hidden">
        {bloggers.map((blogger) => (
          <BloggerMobileCard key={blogger.id} blogger={blogger} onSelect={onSelectBlogger} />
        ))}
      </div>

      <Pagination
        page={page}
        pageSize={BLOGGERS_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function SortIcon({ active, order }: { active: boolean; order: SortOrder | undefined }) {
  if (!active || order === undefined) {
    return <ArrowUpDown size={13} className="text-app-hint/60" aria-hidden="true" />
  }
  return order === 'asc' ? (
    <ArrowUp size={13} aria-hidden="true" />
  ) : (
    <ArrowDown size={13} aria-hidden="true" />
  )
}

function BloggerMobileCard({
  blogger,
  onSelect,
}: {
  blogger: BloggerListItem
  onSelect: (id: number) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(blogger.id)}
      className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3 text-left transition-colors duration-150 ease-out hover:bg-brand-subtle"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <span className="font-medium text-app-text">{blogger.fullName}</span>
          <span className="text-xs text-app-hint">@{blogger.instagramUsername}</span>
        </div>
        <StageBadge stage={blogger.stage} className="shrink-0" />
      </div>
      <dl className="grid grid-cols-3 gap-2 text-xs">
        <MobileStat label="Obunachi" value={formatNumber(blogger.followers)} />
        <MobileStat label="Video" value={formatNumber(blogger.videoCount)} />
        <MobileStat label="Ishlagan" value={formatSom(blogger.totalEarned)} />
      </dl>
    </button>
  )
}

function MobileStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-app-hint">{label}</dt>
      <dd className="tabular font-medium text-app-text">{value}</dd>
    </div>
  )
}

function BloggerListSkeleton() {
  return (
    // `aria-busy` — skrinrider uchun: skelet paytida sahifa jim turmasin, "yuklanmoqda"
    // holati e'lon qilinsin, aks holda kontent birdan paydo bo'lganda foydalanuvchi
    // hech qanday signal olmaydi.
    <div className="flex flex-col gap-3" aria-busy="true">
      {/* Desktop skeleton */}
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        <div className="flex h-10 items-center gap-4 border-b border-app-border bg-app-card px-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={`head-${index}`} className={cn('h-3', index === 0 ? 'w-32' : 'w-16')} />
          ))}
        </div>
        {Array.from({ length: 8 }, (_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="flex h-12 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            {Array.from({ length: 6 }, (_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                className={cn('h-3', colIndex === 0 ? 'w-32' : 'w-16')}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Mobil skeleton */}
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-[92px]" />
        ))}
      </div>
    </div>
  )
}
